import {
  Button,
  Card,
  CardContent,
  ConfirmSheet,
  FormField,
  Input,
  PageHeader,
  SelectField,
  StatusChip,
} from "@jod/ui";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { OwnerDataTable as DataTable } from "../../components/owner-data-table.tsx";
import {
  useCreateMenuCategory,
  useCreateMenuItem,
  useDeleteMenuCategory,
  useDeleteMenuItem,
  useOwnerState,
  useUpdateMenuCategory,
  useUpdateMenuItem,
} from "../../features/backoffice/queries.ts";
import type { MenuCategory, MenuItemRecord } from "../../features/backoffice/types.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/menu")({ component: MenuScreen });

function MenuScreen() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data } = useOwnerState();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [newCategoryName, setNewCategoryName] = useState("Mock Specials");
  const [newCategoryNameTh, setNewCategoryNameTh] = useState("เมนูพิเศษ");
  const createCategory = useCreateMenuCategory();
  const create = useCreateMenuItem();
  const updateCategory = useUpdateMenuCategory();
  const update = useUpdateMenuItem();
  const deleteCategory = useDeleteMenuCategory();
  const deleteItem = useDeleteMenuItem();
  const [pendingDeleteCategory, setPendingDeleteCategory] = useState<MenuCategory | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<MenuItemRecord | null>(null);
  const categories = data?.categories ?? [];
  const items = data?.menuItems ?? [];
  const visibleItems =
    categoryFilter === "all" ? items : items.filter((item) => item.categoryId === categoryFilter);
  const categoryOptions = [
    { value: "all", label: "All categories" },
    ...categories.map((category) => ({ value: category.id, label: category.name })),
  ];
  const categoryName = (categoryId: string) =>
    categories.find((category) => category.id === categoryId)?.name ?? "Uncategorized";

  if (pathname.startsWith("/menu/")) return <Outlet />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Menu"
        subtitle="Categories, items, giftable/alcohol flags, and in-flight warnings."
        actions={
          <Button
            onClick={() =>
              create.mutate({
                name: "New Alcohol Gift",
                nameTh: "ของขวัญแอลกอฮอล์",
                categoryId: categoryFilter === "all" ? categories[0]?.id : categoryFilter,
                giftable: true,
                alcohol: true,
                modifiers: [
                  {
                    id: "mod-ice",
                    name: "Ice preference",
                    required: false,
                    multiSelect: false,
                    options: [{ id: "less-ice", name: "Less ice", priceDeltaMinor: 0 }],
                  },
                ],
              })
            }
          >
            Create menu item
          </Button>
        }
      />
      <Card>
        <CardContent className="space-y-4 p-5">
          <div>
            <h2 className="font-display font-semibold text-lg">Categories</h2>
            <p className="text-muted-foreground text-sm">
              Local setup controls for menu grouping before backend taxonomy endpoints exist.
            </p>
          </div>
          {deleteCategory.error ? (
            <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
              {deleteCategory.error instanceof Error
                ? deleteCategory.error.message
                : "Could not delete category."}
            </p>
          ) : null}
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
            <FormField id="newCategoryName" label="New category EN">
              <Input
                id="newCategoryName"
                value={newCategoryName}
                onChange={(event) => setNewCategoryName(event.target.value)}
              />
            </FormField>
            <FormField id="newCategoryNameTh" label="New category TH">
              <Input
                id="newCategoryNameTh"
                value={newCategoryNameTh}
                onChange={(event) => setNewCategoryNameTh(event.target.value)}
              />
            </FormField>
            <div className="flex items-end">
              <Button
                className="w-full"
                onClick={() =>
                  createCategory.mutate({
                    name: newCategoryName,
                    nameTh: newCategoryNameTh,
                    sortOrder: categories.length + 1,
                  })
                }
              >
                Add category
              </Button>
            </div>
          </div>
          <DataTable
            rows={categories}
            getRowKey={(row) => row.id}
            columns={[
              {
                key: "name",
                header: "Category",
                cell: (row) => (
                  <div className="grid gap-2">
                    <Input
                      aria-label={`${row.name} English name`}
                      defaultValue={row.name}
                      onBlur={(event) =>
                        updateCategory.mutate({
                          categoryId: row.id,
                          input: { name: event.target.value },
                        })
                      }
                    />
                    <Input
                      aria-label={`${row.name} Thai name`}
                      defaultValue={row.nameTh}
                      onBlur={(event) =>
                        updateCategory.mutate({
                          categoryId: row.id,
                          input: { nameTh: event.target.value },
                        })
                      }
                    />
                  </div>
                ),
              },
              {
                key: "sort",
                header: "Sort",
                cell: (row) => (
                  <Input
                    aria-label={`${row.name} sort order`}
                    type="number"
                    defaultValue={row.sortOrder}
                    onBlur={(event) =>
                      updateCategory.mutate({
                        categoryId: row.id,
                        input: { sortOrder: Number(event.target.value) },
                      })
                    }
                  />
                ),
              },
              {
                key: "items",
                header: "Items",
                cell: (row) => items.filter((item) => item.categoryId === row.id).length,
              },
              {
                key: "actions",
                header: "Actions",
                cell: (row) => (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setPendingDeleteCategory(row)}
                  >
                    Delete
                  </Button>
                ),
              },
            ]}
          />
        </CardContent>
      </Card>
      <div className="max-w-sm">
        <SelectField
          id="categoryFilter"
          label="Filter menu category"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={categoryOptions}
        />
      </div>
      <DataTable
        rows={visibleItems}
        getRowKey={(row) => row.id}
        columns={[
          {
            key: "name",
            header: "Name",
            cell: (row) => (
              <button
                type="button"
                className="text-left font-medium"
                onClick={() => navigate({ to: "/menu/$itemId", params: { itemId: row.id } })}
              >
                {row.name}
                <br />
                <span className="text-muted-foreground text-xs">{row.nameTh}</span>
              </button>
            ),
          },
          { key: "category", header: "Category", cell: (row) => categoryName(row.categoryId) },
          { key: "price", header: "Price", cell: (row) => formatMoney(row.priceMinor) },
          {
            key: "flags",
            header: "Flags",
            cell: (row) => (
              <div className="flex flex-wrap gap-1">
                {row.available ? (
                  <StatusChip tone="success">available</StatusChip>
                ) : (
                  <StatusChip tone="neutral">unavailable</StatusChip>
                )}
                {row.giftable ? <StatusChip tone="info">giftable</StatusChip> : null}
                {row.alcohol ? <StatusChip tone="warning">alcohol</StatusChip> : null}
                {row.featuredGiftable ? (
                  <StatusChip tone="success">featured gift</StatusChip>
                ) : null}
              </div>
            ),
          },
          {
            key: "modifiers",
            header: "Modifiers",
            cell: (row) =>
              row.modifiers.length
                ? `${row.modifiers.length} groups / ${row.modifiers.reduce(
                    (sum, group) => sum + group.options.length,
                    0,
                  )} options`
                : "No modifiers",
          },
          { key: "warning", header: "Warning", cell: (row) => row.inFlightWarning ?? "—" },
          {
            key: "actions",
            header: "Actions",
            cell: (row) => (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground"
                  onClick={() =>
                    update.mutate({ itemId: row.id, input: { available: !row.available } })
                  }
                >
                  Toggle availability
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    update.mutate({ itemId: row.id, input: { giftable: !row.giftable } })
                  }
                >
                  Toggle giftable
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground"
                  onClick={() =>
                    update.mutate({ itemId: row.id, input: { alcohol: !row.alcohol } })
                  }
                >
                  Toggle alcohol
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-foreground"
                  onClick={() =>
                    update.mutate({
                      itemId: row.id,
                      input: { featuredGiftable: !row.featuredGiftable },
                    })
                  }
                >
                  Feature gift
                </Button>
                <Button size="sm" variant="destructive" onClick={() => setPendingDeleteItem(row)}>
                  Delete
                </Button>
              </div>
            ),
          },
        ]}
      />
      {deleteItem.error ? (
        <p role="alert" className="rounded-md bg-destructive/10 p-3 text-destructive text-sm">
          {deleteItem.error instanceof Error ? deleteItem.error.message : "Could not delete item."}
        </p>
      ) : null}
      <ConfirmSheet
        open={pendingDeleteCategory !== null}
        onOpenChange={(open) => !open && setPendingDeleteCategory(null)}
        title={`Delete ${pendingDeleteCategory?.name ?? "category"}?`}
        description="Categories with menu items cannot be deleted — move or archive the items first. This cannot be undone."
        confirmLabel="Delete category"
        destructive
        onConfirm={() => pendingDeleteCategory && deleteCategory.mutate(pendingDeleteCategory.id)}
      />
      <ConfirmSheet
        open={pendingDeleteItem !== null}
        onOpenChange={(open) => !open && setPendingDeleteItem(null)}
        title={`Delete ${pendingDeleteItem?.name ?? "item"}?`}
        description="The item is archived (soft-deleted), never hard-deleted, so past orders keep showing it correctly. Rejected while the item is on a still-open order."
        confirmLabel="Delete item"
        destructive
        onConfirm={() => pendingDeleteItem && deleteItem.mutate(pendingDeleteItem.id)}
      />
    </div>
  );
}
